import { Building2, LogOut, Monitor, Moon, Plus, Sun } from 'lucide-react';
import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router';

import { isApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/states';
import { SessionProvider, useLogout, useMeQuery, useSession } from '@/features/auth/session';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';

import { useTheme, type ThemePreference } from './theme';

/** Resolves /support/auth/me once; 401 → sign-in. */
export function AuthGuard() {
  const me = useMeQuery();
  const location = useLocation();
  if (me.isPending) {
    return (
      <div className="flex h-dvh items-center justify-center text-ink3" role="status">
        Loading…
      </div>
    );
  }
  if (me.isError) {
    if (isApiError(me.error) && (me.error.status === 401 || me.error.code === 'UNAUTHENTICATED')) {
      const from = `${location.pathname}${location.search}`;
      return <Navigate to={`/sign-in${from !== '/' ? `?from=${encodeURIComponent(from)}` : ''}`} replace />;
    }
    return (
      <div className="p-6">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </div>
    );
  }
  return (
    <SessionProvider value={me.data}>
      <Outlet />
    </SessionProvider>
  );
}

function ThemeButton({ value, icon: Icon, label }: { value: ThemePreference; icon: typeof Sun; label: string }) {
  const { preference, setPreference } = useTheme();
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={preference === value}
      onClick={() => setPreference(value)}
      className={cn('rounded-md p-1.5 text-nav-ink hover:bg-white/10', preference === value && 'bg-white/10 text-white')}
    >
      <Icon className="size-4" />
    </button>
  );
}

/** Dark top bar (the office app's nav ink) so a support tab is unmistakable next to a tenant tab. */
export function Shell() {
  const admin = useSession();
  const logout = useLogout();
  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <header className="flex items-center gap-4 bg-nav px-5 py-2.5 text-nav-ink">
        <Link to="/" className="flex items-center gap-2.5 font-cond text-[18px] font-bold text-white">
          <span className="relative size-[24px] rounded-[6px] bg-orange">
            <span className="absolute right-1.5 bottom-1.5 left-1.5 h-[4px] rounded-sm bg-nav" />
          </span>
          SiteDay <span className="rounded bg-orange px-1.5 py-0.5 text-[10px] font-extrabold tracking-wider text-on-orange uppercase">Support</span>
        </Link>
        <nav className="flex items-center gap-1 text-sm font-semibold" aria-label="Main">
          <NavLink
            to="/"
            end
            className={({ isActive }) => cn('flex items-center gap-1.5 rounded-md px-2.5 py-1.5 hover:bg-white/5 hover:text-white', isActive && 'bg-nav2 text-white')}
          >
            <Building2 className="size-4" /> Companies
          </NavLink>
          <NavLink
            to="/companies/new"
            className={({ isActive }) => cn('flex items-center gap-1.5 rounded-md px-2.5 py-1.5 hover:bg-white/5 hover:text-white', isActive && 'bg-nav2 text-white')}
          >
            <Plus className="size-4" /> New company
          </NavLink>
        </nav>
        <div className="ml-auto flex items-center gap-1">
          <ThemeButton value="light" icon={Sun} label="Light theme" />
          <ThemeButton value="dark" icon={Moon} label="Dark theme" />
          <ThemeButton value="system" icon={Monitor} label="System theme" />
        </div>
        <div className="flex items-center gap-2 border-l border-nav-line pl-4 text-xs">
          <span className="flex size-7 items-center justify-center rounded-full bg-orange text-[11px] font-extrabold text-on-orange">
            {initials(admin.name)}
          </span>
          <span className="hidden sm:block">
            <b className="block text-nav-ink-hi">{admin.name}</b>
            {admin.email}
          </span>
          <Button variant="ghost" size="sm" className="text-nav-ink hover:bg-white/10 hover:text-white" onClick={() => logout.mutate()} loading={logout.isPending}>
            <LogOut /> Sign out
          </Button>
        </div>
      </header>
      <main className="mx-auto w-full max-w-[1200px] flex-1 px-5 py-6">
        <Outlet />
      </main>
    </div>
  );
}
